<script>
  import { getContext } from 'svelte';
  import '@awesome.me/webawesome-pro/dist/components/card/card.js';
  import '@awesome.me/webawesome-pro/dist/components/badge/badge.js';
  import { getCategoryName } from '#state/categories';

  let { asset } = $props();

  // Consume the context provided by App.svelte
  const openAssetModal = getContext('openAssetModal');

  function getFormattedDate(dateStr) {
    if (dateStr === null || dateStr === undefined || dateStr === '') {
      return 'Unknown Date';
    }
    return dateStr.split(' ')[0];
  }

  function handleImageError(e) {
    if (e !== null && e.target !== null) {
      e.target.style.display = 'none';
    }
  }

  function handleOpenModal() {
    // Fire the global function, passing this card's specific ID
    openAssetModal(asset.asset_id);
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenModal();
    }
  }
</script>

<wa-card class="asset-card">
  <div
    class="interactive-area"
    role="button"
    tabindex="0"
    onclick={handleOpenModal}
    onkeydown={handleKeydown}
  >
    <div class="icon-container">
      {#if asset.icon_url !== null && asset.icon_url !== undefined && asset.icon_url !== ''}
        <img
          src={asset.icon_url}
          alt={asset.title}
          class="asset-icon"
          onerror={handleImageError}
        />
      {:else}
        <div class="icon-placeholder"></div>
      {/if}
    </div>

    <div class="info-container">
      <div class="title-row">
        <h3 class="title">{asset.title}</h3>
      </div>

      <div class="badges-row">
        {#if asset.category_id !== null && asset.category_id !== undefined}
          <wa-badge variant="neutral" size="small">{getCategoryName(asset.category_id)}</wa-badge>
        {/if}

        {#if asset.godot_version !== null && asset.godot_version !== undefined}
          <wa-badge variant="primary" size="small">{asset.godot_version}</wa-badge>
        {/if}

        {#if asset.support_level !== null && asset.support_level !== undefined && asset.support_level !== ''}
          <wa-badge variant="neutral" size="small">{asset.support_level}</wa-badge>
        {/if}

        {#if asset.cost !== null && asset.cost !== undefined && asset.cost !== ''}
          <wa-badge variant="success" size="small">{asset.cost}</wa-badge>
        {/if}
      </div>
    </div>
  </div>

  <div slot="footer" class="card-footer">
    <div class="author">
      {asset.author || 'Unknown'}
    </div>
    <div class="version-info">
      v{asset.version_string || '0.0.0'} | {getFormattedDate(asset.modify_date)}
    </div>
  </div>
</wa-card>

<style>
  .asset-card {
    width: 100%;
    --padding: 1rem;
  }

  .interactive-area {
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;
    /* Pull the container out to the edges of the card body's native padding */
    margin: -1rem;
    /* Push the internal content back to exactly where it was */
    padding: 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    /* Match standard card border radius for the top corners */
    border-top-left-radius: var(--wa-border-radius-m, 0.25rem);
    border-top-right-radius: var(--wa-border-radius-m, 0.25rem);
  }

  .interactive-area:hover,
  .interactive-area:focus-visible {
    background-color: var(--wa-color-neutral-50);
    outline: none;
  }

  .interactive-area:focus-visible {
    box-shadow: inset 0 0 0 2px var(--wa-color-primary-500);
  }

  .icon-container {
    flex: 0 0 72px;
    width: 72px;
    height: 72px;
    border-radius: var(--wa-border-radius-m);
    overflow: hidden;
    background-color: var(--wa-color-neutral-100);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-placeholder {
    width: 100%;
    height: 100%;
    background-color: var(--wa-color-neutral-200);
  }

  .asset-icon {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .info-container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: flex-start;
  }

  .title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    line-height: 1.3;
    /* Explicitly allowing text to wrap naturally */
    white-space: normal;
    word-break: break-word;
    transition: color 0.2s ease;
  }

  .interactive-area:hover .title,
  .interactive-area:focus-visible .title {
    color: var(--wa-color-primary-600);
    text-decoration: underline;
  }

  .badges-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.85rem;
    color: var(--wa-color-neutral-600);
  }

  .author {
    font-weight: 600;
    color: var(--wa-color-neutral-800);
  }

  .version-info {
    font-family: monospace;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
  }
</style>